import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Send, Bot, ShoppingCart, Package, Tag, CheckCircle2, RotateCcw } from "lucide-react";
import { API } from "@/lib/apiClient";

const fmt = (cur, n) =>
  `${cur === "IDR" ? "Rp" : cur + " "}${Number(n || 0).toLocaleString("id-ID")}`;

const sidKey = (token) => `aisales_sid_${token}`;

function randomId() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch (e) {}
  return "sess-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function persistSessionId(token, sid) {
  try {
    localStorage.setItem(sidKey(token), sid);
  } catch (e) {}
}

function resolveSessionId(propId, token) {
  if (propId) return propId;
  try {
    const existing = localStorage.getItem(sidKey(token));
    if (existing) return existing;
  } catch (e) {}
  const fresh = randomId();
  persistSessionId(token, fresh);
  return fresh;
}

function renderText(text) {
  const parts = String(text || "").split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**") ? <strong key={i}>{p.slice(2, -2)}</strong> : <span key={i}>{p}</span>
  );
}

const keyMatches = (k, needles) => needles.some((n) => String(k).toLowerCase().includes(n));
const rowValue = (row, needles) => {
  for (const [k, v] of Object.entries(row || {})) {
    if (keyMatches(k, needles)) return v;
  }
  return undefined;
};
const toNum = (v) => {
  if (v === undefined || v === null || v === "" || typeof v === "boolean") return undefined;
  const n = typeof v === "number" ? v : parseFloat(String(v).replace(/,/g, ""));
  return Number.isFinite(n) ? n : undefined;
};
const firstText = (row) => {
  for (const v of Object.values(row || {})) {
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return "";
};

function summarizeProduct(p) {
  if (!p || typeof p !== "object") return { title: String(p ?? ""), stockUnknown: true };
  let title = rowValue(p, ["name", "nama", "title", "judul", "produk"]);
  if (typeof title !== "string" || !title.trim()) title = firstText(p);
  let image = rowValue(p, ["image", "img", "foto", "gambar", "thumbnail", "picture", "photo"]);
  if (typeof image !== "string" || !image.startsWith("http")) image = "";
  let url = rowValue(p, ["url", "link", "href"]);
  if (typeof url !== "string" || !url.startsWith("http")) url = "";
  const price = toNum(rowValue(p, ["price", "harga"]));
  const stock = toNum(rowValue(p, ["stock", "stok", "qty", "quantity"]));
  return { title: String(title || "Produk"), price, stock, stockUnknown: stock === undefined, image, url };
}

function ProductCard({ p, cur, accent }) {
  const s = summarizeProduct(p);
  const out = !s.stockUnknown && s.stock <= 0;
  return (
    <div data-testid="chat-product-card" className="rounded-xl border border-border bg-card p-3 mt-2 flex gap-3">
      {s.image ? (
        <img data-testid="chat-product-image" src={s.image} alt={s.title} loading="lazy"
          className="w-11 h-11 rounded-lg object-cover shrink-0 border border-border" />
      ) : (
        <div className="w-11 h-11 rounded-lg flex items-center justify-center shrink-0" style={{ background: accent + "22" }}>
          <Package className="w-5 h-5" style={{ color: accent }} />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-sm leading-tight truncate">{s.title}</p>
        {s.price !== undefined && (
          <p className="text-sm font-display font-bold" style={{ color: accent }}>{fmt(cur, s.price)}</p>
        )}
        {/* {s.stockUnknown ? (
          <p className="text-xs text-muted-foreground">Stok tidak diketahui</p>
        ) : (
          <p className={`text-xs ${out ? "text-destructive" : "text-emerald-600"}`}>
            {out ? "Stok habis" : `Stok ${s.stock}`}
          </p>
        )} */}
      </div>
      {s.url && (
        <a href={s.url} target="_blank" rel="noreferrer"
          className="self-center text-xs font-medium rounded-lg px-2.5 py-1.5 text-white flex items-center gap-1 shrink-0"
          style={{ background: out ? "#9ca3af" : accent, pointerEvents: out ? "none" : "auto" }}>
          <ShoppingCart className="w-3 h-3" /> Beli
        </a>
      )}
    </div>
  );
}

function OrderCard({ o }) {
  const colors = {
    "Selesai": "text-emerald-600 bg-emerald-500/10",
    "Sedang Dikirim": "text-blue-600 bg-blue-500/10",
    "Diproses": "text-amber-600 bg-amber-500/10",
    "Dibatalkan": "text-destructive bg-destructive/10",
  };
  const code = rowValue(o, ["order_code", "code", "order_no", "invoice"]) ?? rowValue(o, ["id"]) ?? "";
  const status = rowValue(o, ["status", "state"]) ?? "";
  const customer = rowValue(o, ["customer_name", "customer", "nama", "buyer"]) ?? "";
  const items = rowValue(o, ["items", "item", "detail"]) ?? "";
  return (
    <div data-testid="chat-order-status-card" className="rounded-xl border border-border bg-card p-3 mt-2">
      <div className="flex items-center justify-between">
        <span className="font-mono text-xs font-semibold">{String(code)}</span>
        {status !== "" && (
          <span className={`text-xs font-medium rounded-full px-2 py-0.5 ${colors[status] || "bg-muted"}`}>{String(status)}</span>
        )}
      </div>
      {customer !== "" && <p className="text-sm mt-1">{String(customer)}</p>}
      {items !== "" && <p className="text-xs text-muted-foreground">{String(items)}</p>}
    </div>
  );
}

export default function ChatWidget({ token, sessionId: sessionIdProp, className = "", accentOverride, showDataSource = false }) {
  const [cfg, setCfg] = useState(null);
  const [cur, setCur] = useState("IDR");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(() => resolveSessionId(sessionIdProp, token));
  const scrollRef = useRef(null);
  const abortRef = useRef(null);
  const sessionRef = useRef(sessionId);
  const accent = accentOverride || cfg?.accent_color || "#2563EB";

  useEffect(() => {
    sessionRef.current = sessionId;
  }, [sessionId]);

  // Mulai percakapan baru: sesi lama dibiarkan utuh di database (terbaca di Riwayat
  // Percakapan admin), sedangkan pengunjung mendapat session id baru yang segar.
  const startNewSession = () => {
    if (abortRef.current) abortRef.current.abort();
    const next = randomId();
    persistSessionId(token, next);
    setSessionId(next);
    // Mode embed: session id aslinya disimpan di localStorage website klien oleh script
    // embed dan dikirim lewat ?sid=. Beri tahu parent agar key di sana ikut diperbarui,
    // supaya setelah reload pengunjung tetap memakai sesi baru.
    try {
      if (window.parent && window.parent !== window) {
        window.parent.postMessage({ type: "aisales:session-reset", token, sid: next }, "*");
      }
    } catch (e) {}
  };

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      axios.get(`${API}/public/config/${token}`),
      sessionId
        ? axios.get(`${API}/public/chat/${token}/messages`, { params: { session_id: sessionId } }).catch(() => ({ data: [] }))
        : Promise.resolve({ data: [] }),
    ])
      .then(([cr, mr]) => {
        if (cancelled) return;
        setCfg(cr.data.config);
        setCur(cr.data.currency);
        const history = Array.isArray(mr.data) ? mr.data : [];
        if (history.length > 0) {
          setMessages(history.map((m) => ({
            role: m.role,
            content: m.content,
            via: m.via,
            source: m.meta?.source,
            products: m.meta?.products,
            order: m.meta?.order,
            promos: m.meta?.promos,
            lead: m.meta?.lead_captured,
          })));
        } else {
          setMessages([{ role: "assistant", content: cr.data.config.welcome_message }]);
        }
      })
      .catch(() => {
        if (!cancelled) setMessages([{ role: "assistant", content: "Widget tidak ditemukan." }]);
      });
    return () => {
      cancelled = true;
    };
  }, [token, sessionId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setBusy(true);

    const idx = { current: null };
    const sidAtSend = sessionId;
    let meta = {};
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const res = await fetch(`${API}/public/chat/${token}/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: msg }),
        signal: controller.signal,
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let started = false;

      const ensureMsg = () => {
        if (!started) {
          started = true;
          setBusy(false);
          setMessages((m) => {
            idx.current = m.length;
            return [...m, { role: "assistant", content: "", ...meta }];
          });
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const evLine = part.split("\n").find((l) => l.startsWith("event:"));
          const dataLine = part.split("\n").find((l) => l.startsWith("data:"));
          if (!dataLine) continue;
          if (sessionRef.current !== sidAtSend) break;
          const payload = JSON.parse(dataLine.slice(5).trim());
          const ev = evLine ? evLine.slice(6).trim() : "token";
          if (ev === "meta") {
            meta = { products: payload.products, order: payload.order, promos: payload.promos, lead: payload.lead_captured, source: payload.source };
            if (payload.currency) setCur(payload.currency);
          } else if (ev === "token") {
            ensureMsg();
            setMessages((m) => m.map((mm, i) => (i === idx.current ? { ...mm, content: mm.content + payload.t } : mm)));
          }
        }
      }
    } catch (e) {
      if (e && e.name === "AbortError") return;
      setMessages((m) => [...m, { role: "assistant", content: "Maaf, terjadi kesalahan. Coba lagi ya." }]);
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
      setBusy(false);
    }
  };

  return (
    <div className={`flex flex-col h-full bg-background overflow-hidden ${className}`}>
      <div className="flex items-center gap-3 p-4 text-white shrink-0" style={{ background: accent }}>
        <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
          <Bot className="w-5 h-5" />
        </div>
        <div className="leading-tight">
          <p className="font-display font-bold text-sm">{cfg?.chatbot_name || "Sales Assistant"}</p>
          <p className="text-xs text-white/80 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-green-300 inline-block" /> Online
          </p>
        </div>
        <button type="button" data-testid="chat-reset-btn" title="Mulai percakapan baru"
          aria-label="Mulai percakapan baru" onClick={startNewSession} disabled={busy}
          className="ml-auto w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center shrink-0 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
          <RotateCcw className="w-4 h-4" />
        </button>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" style={{ background: "hsl(var(--muted))" }}>
        {messages.map((m, i) => (
          <div key={i} className={`msg-in flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] ${m.role === "user" ? "" : "w-full"}`}>
              <div className={`rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                m.role === "user" ? "text-white rounded-br-sm" : "bg-card border border-border rounded-bl-sm"
              }`} style={m.role === "user" ? { background: accent } : {}}>
                {m.role === "assistant" ? renderText(m.content) : m.content}
              </div>
              {m.role === "assistant" && showDataSource && m.source && (
                <div className="mt-1.5 flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground" data-testid="chat-data-source">
                  <span className={`w-1.5 h-1.5 rounded-full ${m.source === "live" ? "bg-emerald-500" : "bg-amber-500"}`} />
                  {m.source === "live" ? "Data: database bisnis" : "Data: internal"}
                </div>
              )}
              {m.order && <OrderCard o={m.order} />}
              {m.products?.map((p) => <ProductCard key={p.id} p={p} cur={cur} accent={accent} />)}
              {m.promos?.length > 0 && (
                <div className="mt-2 space-y-1.5">
                  {m.promos.map((pr) => (
                    <div key={pr.id} className="rounded-xl border border-dashed p-2.5 flex items-center gap-2"
                      style={{ borderColor: accent }}>
                      <Tag className="w-4 h-4" style={{ color: accent }} />
                      <div>
                        <span className="font-mono font-bold text-xs" style={{ color: accent }}>{pr.code}</span>
                        <p className="text-xs text-muted-foreground">{pr.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {m.lead && (
                <div className="mt-2 flex items-center gap-1.5 text-xs text-emerald-600">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Tim kami akan segera menghubungimu.
                </div>
              )}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex justify-start msg-in">
            <div className="bg-card border border-border rounded-2xl rounded-bl-sm px-4 py-3 flex gap-1">
              {[0, 1, 2].map((i) => (
                <span key={i} className="typing-dot w-1.5 h-1.5 rounded-full bg-muted-foreground"
                  style={{ animationDelay: `${i * 0.15}s` }} />
              ))}
            </div>
          </div>
        )}
      </div>

      {messages.length <= 1 && cfg?.quick_prompts?.length > 0 && (
        <div className="px-3 pb-2 flex flex-wrap gap-1.5 shrink-0" style={{ background: "hsl(var(--muted))" }}>
          {cfg.quick_prompts.map((q, i) => (
            <button key={i} data-testid="chat-quick-prompt" onClick={() => send(q)}
              className="text-xs rounded-full border border-border bg-card px-3 py-1.5 hover:border-current transition-colors"
              style={{ color: accent }}>
              {q}
            </button>
          ))}
        </div>
      )}

      <div className="p-3 border-t border-border bg-card shrink-0 flex gap-2">
        <input data-testid="chat-input-field" value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Ketik pesan..."
          className="flex-1 bg-muted rounded-full px-4 py-2.5 text-sm outline-none focus:ring-2"
          style={{ "--tw-ring-color": accent }} />
        <button data-testid="chat-send-btn" onClick={() => send()} disabled={busy}
          className="w-10 h-10 rounded-full flex items-center justify-center text-white shrink-0 disabled:opacity-50"
          style={{ background: accent }}>
          <Send className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
